import { ColumnDef } from "@tanstack/react-table";
import clsx from "clsx";

import { SiteErrorBreakdownItem } from "~/api/Api";
import Card from "~/components/ui/card/Card";
import DataTable, {
  DataTableColumnMeta,
} from "~/components/ui/dataTable/DataTable";

import { formatChartValue } from "./BreakdownChart";
import styles from "./BreakdownTable.module.scss";

type ErrorsBreakdownTableProps = {
  data: SiteErrorBreakdownItem[];
  isLoading: boolean;
};

const CATEGORY_LABEL: Record<SiteErrorBreakdownItem["category"], string> = {
  origin_down: "Origin Down",
  origin_reset: "Origin Reset",
  origin_timeout: "Origin Timeout",
  dns_failure: "DNS Failure",
  tls_error: "TLS Error",
  origin_closed: "Origin Closed",
  network_error: "Network Error",
  protocol_error: "Protocol Error",
  backend: "Backend",
};

function statusClass(status: number) {
  if (status >= 500) return styles.status5xx;
  if (status >= 400) return styles.status4xx;
  if (status >= 300) return styles.status3xx;
  if (status >= 200) return styles.status2xx;

  return undefined;
}

const columns: ColumnDef<SiteErrorBreakdownItem>[] = [
  {
    accessorKey: "status",
    header: "Status",
    size: 100,
    cell: ({ row }) => {
      const status = row.original.status;

      return <span className={statusClass(status)}>{status || "—"}</span>;
    },
  },
  {
    accessorKey: "kind",
    header: "Kind",
    size: 130,
    cell: ({ row }) =>
      row.original.kind === "connection" ? (
        <span className={styles.kindConnection}>Connection</span>
      ) : (
        <span className={styles.kindBackend}>Backend</span>
      ),
  },
  {
    accessorKey: "category",
    header: "Category",
    cell: (info) =>
      CATEGORY_LABEL[info.getValue() as SiteErrorBreakdownItem["category"]],
  },
  {
    accessorKey: "bot",
    header: "Origin",
    size: 100,
    cell: ({ row }) =>
      row.original.bot ? (
        <span className={styles.botBadge}>Bot</span>
      ) : (
        <span className={styles.humanBadge}>Human</span>
      ),
  },
  {
    accessorKey: "total",
    header: "Count",
    size: 100,
    meta: { align: "right" } satisfies DataTableColumnMeta,
    cell: (info) => (
      <span className={styles.totalValue}>
        {formatChartValue(info.getValue() as number)}
      </span>
    ),
  },
];

const ErrorsBreakdownTable = ({
  data,
  isLoading,
}: ErrorsBreakdownTableProps) => (
  <div className={clsx(styles.root)}>
    <Card title="Error Breakdown">
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        skeletonRows={6}
        emptyText="No errors for this period."
        pageSize={20}
        initialSorting={[{ id: "total", desc: true }]}
      />
    </Card>
  </div>
);

export default ErrorsBreakdownTable;
